import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Row {
  productId: string;
  productName: string;
  category: string;
  quantityOnHand: number;
}

interface Line {
  productId: string;
  productName: string;
  quantity: number;
}

/**
 * Dough & Soul bakes and supplies the Breadbin Outlet, so transfers run in
 * one direction most days. Stock leaves the sending branch immediately and
 * lands at the receiving one.
 */
export default function TransfersPanel({
  branchId,
  branchName,
  staffName,
}: {
  branchId: string;
  branchName?: string;
  staffName?: string;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [toBranchId, setToBranchId] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const [inv, mv, br] = await Promise.all([
        apiClient.getInventory(branchId),
        apiClient.getInventoryMovements(branchId).catch(() => ({ data: [] })),
        apiClient.getBranches().catch(() => ({ data: [] })),
      ]);
      const others = (Array.isArray(br.data) ? br.data : []).filter((b: any) => b.id !== branchId);
      setBranches(others);
      setToBranchId((prev) => prev || others[0]?.id || '');
      setRows(Array.isArray(inv.data) ? inv.data : []);
      const list = Array.isArray(mv.data) ? mv.data : mv.data?.movements || [];
      setMovements(list.filter((m: any) => String(m.movementType || '').includes('transfer')).slice(0, 15));
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load stock');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const addLine = (r: Row) => {
    setLines((prev) =>
      prev.some((l) => l.productId === r.productId)
        ? prev.map((l) => (l.productId === r.productId ? { ...l, quantity: l.quantity + 1 } : l))
        : [...prev, { productId: r.productId, productName: r.productName, quantity: 1 }],
    );
    setSearch('');
  };

  const setQty = (productId: string, q: number) =>
    setLines((prev) => prev.map((l) => (l.productId === productId ? { ...l, quantity: q } : l)));

  const removeLine = (productId: string) =>
    setLines((prev) => prev.filter((l) => l.productId !== productId));

  const send = async () => {
    const valid = lines.filter((l) => l.quantity > 0);
    if (valid.length === 0) {
      setErr('Add at least one product to transfer.');
      return;
    }
    if (!toBranchId) {
      setErr('Choose where the stock is going.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await apiClient.transferInventory(
        branchId,
        toBranchId,
        valid.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        reason.trim() || `Transfer by ${staffName || 'staff'}`,
      );
      setMsg(`Transferred ${valid.length} product${valid.length === 1 ? '' : 's'}.`);
      setLines([]);
      setReason('');
      await load();
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading stock…</div>;

  const matches = search.trim()
    ? rows.filter((r) => r.productName.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Send stock out</h3>
          <p className="text-sm text-gray-500">From {branchName || 'this branch'}</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
            <select
              value={toBranchId}
              onChange={(e) => setToBranchId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              {branches.length === 0 && <option value="">No other branch found</option>}
              {branches.map((b: any) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a product to add…"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            {matches.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                {matches.map((r) => (
                  <button
                    key={r.productId}
                    onClick={() => addLine(r)}
                    className="w-full text-left px-4 py-2.5 hover:bg-amber-50 flex justify-between items-center"
                  >
                    <span className="font-medium text-gray-900">{r.productName}</span>
                    <span className="text-sm text-gray-500 tabular-nums">{r.quantityOnHand} on hand</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {lines.length === 0 ? (
            <p className="text-gray-500 text-sm py-6 text-center">Nothing added yet.</p>
          ) : (
            <div className="border rounded-lg divide-y">
              {lines.map((l) => {
                const stock = rows.find((r) => r.productId === l.productId)?.quantityOnHand ?? 0;
                return (
                  <div key={l.productId} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{l.productName}</p>
                      <p className="text-xs text-gray-500 tabular-nums">
                        {stock} on hand → {stock - l.quantity} after
                      </p>
                    </div>
                    <input
                      type="number"
                      min={1}
                      value={l.quantity}
                      onChange={(e) => setQty(l.productId, parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg tabular-nums text-right font-semibold"
                    />
                    <button
                      onClick={() => removeLine(l.productId)}
                      className="text-red-500 hover:text-red-700 font-bold px-2"
                      aria-label={`Remove ${l.productName}`}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. morning delivery"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={send}
            disabled={busy || lines.length === 0}
            className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded-lg"
          >
            {busy ? 'Sending…' : 'Send transfer'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 px-6 py-4 border-b">Recent transfers</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">When</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Product</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Direction</th>
                <th className="text-right py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Qty</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m: any, i: number) => (
                <tr key={m.id || i} className="border-b last:border-0">
                  <td className="py-2.5 px-6 text-gray-600">
                    {m.createdAt ? new Date(m.createdAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="py-2.5 px-4 font-medium text-gray-900">{m.productName || m.product?.name || '—'}</td>
                  <td className="py-2.5 px-4 text-gray-600">
                    {String(m.movementType).includes('out') ? 'Sent out' : 'Received'}
                  </td>
                  <td
                    className={`py-2.5 px-6 text-right tabular-nums font-semibold ${
                      (m.quantityDelta ?? 0) < 0 ? 'text-red-600' : 'text-green-700'
                    }`}
                  >
                    {(m.quantityDelta ?? 0) > 0 ? '+' : ''}
                    {m.quantityDelta ?? '—'}
                  </td>
                </tr>
              ))}
              {movements.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-500">
                    No transfers recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
